import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import AdminHeader from "@/components/feature/AdminHeader";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import { logAudit } from "@/lib/logAudit";
import { createNotification } from "@/lib/createNotification";
import { queueEmail, paymentReceivedBody } from "@/lib/queueEmail";
import { createReminder, daysAfter } from "@/lib/createReminder";
import { calculateContractBalance } from "@/lib/contractBalance";
import PaymentModal, { type Payment } from "./components/PaymentModal";
import StatusBadge from "@/components/base/StatusBadge";

type ClientOption = { id: string; full_name: string; email: string };
type ContractOption = { id: string; client_id: string; contract_number: string };

const METHOD_ICONS: Record<string, string> = {
  zelle: "ri-exchange-dollar-line",
  card: "ri-bank-card-line",
  transfer: "ri-bank-line",
  cash: "ri-money-dollar-circle-line",
  venmo: "ri-smartphone-line",
  check: "ri-file-list-3-line",
  other: "ri-more-2-line",
};

const METHOD_OPTIONS = ["all", "cash", "card", "zelle", "venmo", "transfer", "check", "other"];
const STATUS_OPTIONS = ["all", "confirmed", "paid", "pending", "void", "cancelled", "refunded"];
const CONFIRMED_STATUSES = ["confirmed", "paid", "completed"];

export default function AdminPayments() {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [clientOptions, setClientOptions] = useState<ClientOption[]>([]);
  const [contractOptions, setContractOptions] = useState<ContractOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Modal
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const fetchAll = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const [paymentsRes, clientsRes, contractsRes] = await Promise.all([
        supabase
          .from("payments")
          .select("*")
          .eq("tenant_id", tid)
          .order("created_at", { ascending: false }),
        supabase
          .from("clients")
          .select("id, full_name, email")
          .eq("tenant_id", tid)
          .eq("status", "active"),
        supabase
          .from("contracts")
          .select("id, client_id, contract_number")
          .eq("tenant_id", tid),
      ]);
      if (paymentsRes.error) throw new Error(paymentsRes.error.message);
      setPayments((paymentsRes.data ?? []) as Payment[]);
      setClientOptions((clientsRes.data ?? []) as ClientOption[]);
      setContractOptions((contractsRes.data ?? []) as ContractOption[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load payments.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchAll(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchAll]);

  /* ── Filtered list ── */
  const filtered = payments.filter((p) => {
    const q = search.toLowerCase();
    const matchSearch =
      (p.client_name ?? "").toLowerCase().includes(q) ||
      (p.reference ?? "").toLowerCase().includes(q) ||
      (p.contract_number ?? "").toLowerCase().includes(q);
    const matchStatus = statusFilter === "all" || p.status === statusFilter;
    const matchMethod = methodFilter === "all" || p.method === methodFilter;
    const matchFrom = !dateFrom || p.payment_date >= dateFrom;
    const matchTo = !dateTo || p.payment_date <= dateTo;
    return matchSearch && matchStatus && matchMethod && matchFrom && matchTo;
  });

  /* ── Stats ── */
  const confirmedPayments = filtered.filter((p) => CONFIRMED_STATUSES.includes(p.status));
  const totalCollected = confirmedPayments.reduce((a, b) => a + Number(b.amount), 0);
  const totalPending = filtered.filter((p) => p.status === "pending").reduce((a, b) => a + Number(b.amount), 0);
  const avgPayment = confirmedPayments.length ? Math.round(totalCollected / confirmedPayments.length) : 0;

  /* ── Handlers ── */
  const handleNew = () => { setSelectedPayment(null); setIsNew(true); setModalOpen(true); };
  const handleEdit = (p: Payment) => { setSelectedPayment(p); setIsNew(false); setModalOpen(true); };

  const handleVoid = async (p: Payment) => {
    const result = await Swal.fire({
      title: "Void this payment?",
      html: `<p style="color:#64748b;font-size:14px;">Payment of <strong>$${Number(p.amount).toLocaleString()}</strong> from <strong>${p.client_name}</strong> will be marked as void.<br><br>This will recalculate the contract balance.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Void Payment",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      background: "#fff",
      color: "#0f172a",
    });
    if (!result.isConfirmed || !tenantId) return;

    const now = new Date().toISOString();
    const { data: updated, error } = await supabase
      .from("payments")
      .update({ status: "void", updated_at: now })
      .eq("id", p.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (!error && updated) {
      setPayments((prev) => prev.map((x) => (x.id === p.id ? (updated as Payment) : x)));
      logAudit({ tenantId, action: "payment_voided", entity: "payment", entityId: p.id, entityName: p.client_name, details: { amount: p.amount } }).catch(() => {});
      if (p.contract_id) {
        await calculateContractBalance(tenantId, p.contract_id, { sync: true });
      }
      await Swal.fire({
        icon: "success",
        title: "Payment Voided",
        text: "The payment has been voided and the contract balance has been updated.",
        timer: 2000,
        showConfirmButton: false,
        background: "#fff",
        color: "#0f172a",
      });
    }
  };

  const handleDelete = async (p: Payment) => {
    const result = await Swal.fire({
      title: "Delete this payment?",
      html: `<p style="color:#64748b;font-size:14px;">Payment of <strong>$${Number(p.amount).toLocaleString()}</strong> from <strong>${p.client_name}</strong> will be permanently deleted.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
      background: "#fff",
      color: "#0f172a",
    });
    if (!result.isConfirmed || !tenantId) return;

    const { error } = await supabase
      .from("payments")
      .delete()
      .eq("id", p.id)
      .eq("tenant_id", tenantId);

    if (!error) {
      setPayments((prev) => prev.filter((x) => x.id !== p.id));
      if (p.contract_id) {
        await calculateContractBalance(tenantId, p.contract_id, { sync: true });
      }
    }
  };

  const handleSave = async (data: Payment) => {
    if (!tenantId) return;

    if (isNew) {
      const { id: _id, created_at: _ca, ...insertData } = data;
      const { data: created, error } = await supabase
        .from("payments")
        .insert({ ...insertData, tenant_id: tenantId })
        .select()
        .maybeSingle();

      if (!error && created) {
        const cp = created as Payment;
        setPayments((prev) => [cp, ...prev]);
        logAudit({ tenantId, action: "payment_created", entity: "payment", entityId: cp.id, entityName: cp.client_name, details: { amount: cp.amount, method: cp.method } }).catch(() => {});
        createNotification({
          tenantId,
          type: "success",
          category: "payment",
          title: "Payment recorded",
          message: `Payment of $${Number(cp.amount).toLocaleString()} from ${cp.client_name} has been registered.`,
          entityType: "payment",
          entityId: cp.id,
        });
        const clientRecord = clientOptions.find((c) => c.id === cp.client_id);
        if (clientRecord?.email) {
          queueEmail({
            tenantId,
            toEmail: clientRecord.email,
            subject: `Payment Confirmation – $${Number(cp.amount).toLocaleString()}`,
            body: paymentReceivedBody({
              clientName: clientRecord.full_name,
              amount: Number(cp.amount),
              method: cp.method,
              reference: cp.reference,
              paymentDate: cp.payment_date,
            }),
          });
        }
        if (cp.status === "pending") {
          createReminder({
            tenantId,
            reminderType: "payment_pending",
            entityType: "payment",
            entityId: cp.id,
            reminderDate: daysAfter(new Date().toISOString(), 7),
            message: `Pending payment of $${Number(cp.amount).toLocaleString()} from ${cp.client_name} — follow up.`,
          });
        }
        if (cp.contract_id) {
          await calculateContractBalance(tenantId, cp.contract_id, { sync: true });
        }
      }
    } else {
      const { data: updated, error } = await supabase
        .from("payments")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", data.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();

      if (!error && updated) {
        const up = updated as Payment;
        setPayments((prev) => prev.map((x) => (x.id === up.id ? up : x)));
        logAudit({ tenantId, action: "payment_updated", entity: "payment", entityId: up.id, entityName: data.client_name }).catch(() => {});
        if (data.contract_id) {
          await calculateContractBalance(tenantId, data.contract_id, { sync: true });
        }
      }
    }

    setModalOpen(false);
  };

  const formatDate = (d: string) => {
    if (!d) return "—";
    try {
      return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return d;
    }
  };

  /* ── Render ── */
  return (
    <AdminLayout>
      <AdminHeader
        title="Payments"
        subtitle="Track all studio payments and receipts"
        actions={
          <button
            onClick={handleNew}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line"></i></span>
            New Payment
          </button>
        }
      />

      <div className="px-4 md:px-8 py-4 md:py-7">
        {/* ── Stats ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Transactions", value: filtered.length.toString(), color: "text-[#0f172a]", icon: "ri-receipt-line" },
            { label: "Total Collected", value: `$${totalCollected.toLocaleString()}`, color: "text-emerald-600", icon: "ri-checkbox-circle-line" },
            { label: "Pending", value: `$${totalPending.toLocaleString()}`, color: "text-amber-500", icon: "ri-time-line" },
            { label: "Avg Payment", value: `$${avgPayment.toLocaleString()}`, color: "text-[#C8A96E]", icon: "ri-bar-chart-line" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-400 text-xs uppercase tracking-wider">{s.label}</p>
                <span className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50">
                  <i className={`${s.icon} text-slate-400 text-sm`}></i>
                </span>
              </div>
              <p className={`font-bold text-2xl ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-5 space-y-3">
          {/* Row 1: search + date range */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search client, reference, contract…"
                className="w-full bg-slate-50 rounded-lg border border-slate-200 pl-9 pr-4 py-2 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/60 transition-colors"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-400 text-xs">From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/60 transition-colors cursor-pointer"
              />
              <span className="text-slate-400 text-xs">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/60 transition-colors cursor-pointer"
              />
              {(dateFrom || dateTo) && (
                <button
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  className="text-slate-400 hover:text-red-400 cursor-pointer text-xs"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
          </div>

          {/* Row 2: status + method pills */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 p-1">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-all cursor-pointer whitespace-nowrap ${
                    statusFilter === s
                      ? "bg-[#C8A96E] text-white"
                      : "text-slate-500 hover:text-[#0f172a]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 p-1">
              {METHOD_OPTIONS.map((m) => (
                <button
                  key={m}
                  onClick={() => setMethodFilter(m)}
                  className={`px-3 py-1 rounded-md text-xs font-medium capitalize transition-all cursor-pointer whitespace-nowrap ${
                    methodFilter === m
                      ? "bg-[#C8A96E] text-white"
                      : "text-slate-500 hover:text-[#0f172a]"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {loading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} onRetry={() => tenantId && fetchAll(tenantId)} showLogin />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Client", "Contract", "Date", "Method", "Reference", "Amount", "Status", ""].map((h) => (
                      <th
                        key={h}
                        className={`text-slate-400 text-xs font-semibold px-5 py-3.5 uppercase tracking-wider ${h === "Amount" ? "text-right" : "text-left"}`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {filtered.map((p) => {
                    const isVoidedOrCancelled = p.status === "void" || p.status === "cancelled";
                    return (
                      <tr
                        key={p.id}
                        className={`hover:bg-slate-50/80 transition-colors ${isVoidedOrCancelled ? "opacity-50" : ""}`}
                      >
                        <td className="px-5 py-3.5">
                          <p className="text-[#0f172a] text-sm font-medium">{p.client_name}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-slate-500 text-sm font-mono text-xs">{p.contract_number || "—"}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-slate-500 text-sm">{formatDate(p.payment_date)}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className="w-6 h-6 flex items-center justify-center text-[#C8A96E]">
                              <i className={`${METHOD_ICONS[p.method] ?? "ri-money-dollar-circle-line"} text-sm`}></i>
                            </span>
                            <span className="text-slate-600 text-sm capitalize">{p.method}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-slate-400 text-xs font-mono">{p.reference || "—"}</p>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <p className={`font-bold text-sm ${isVoidedOrCancelled ? "line-through text-slate-400" : "text-[#0f172a]"}`}>
                            ${Number(p.amount).toLocaleString()}
                          </p>
                        </td>
                        <td className="px-5 py-3.5">
                          <StatusBadge status={p.status} />
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={() => handleEdit(p)}
                              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                              title="Edit"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            {!isVoidedOrCancelled && (
                              <button
                                onClick={() => handleVoid(p)}
                                className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-amber-500 hover:bg-amber-50 rounded-lg transition-all cursor-pointer"
                                title="Void payment"
                              >
                                <i className="ri-forbid-line text-sm"></i>
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(p)}
                              className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                              title="Delete"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filtered.length === 0 && !loading && (
                    <tr>
                      <td colSpan={8} className="py-16">
                        <div className="flex flex-col items-center gap-3 text-center">
                          <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-[#C8A96E]/10">
                            <i className="ri-money-dollar-circle-line text-[#C8A96E] text-2xl"></i>
                          </div>
                          <div>
                            <p className="text-[#0f172a] font-medium text-sm mb-1">
                              {payments.length === 0 ? "No payments yet" : "No payments match your filters"}
                            </p>
                            <p className="text-slate-400 text-xs">
                              {payments.length === 0
                                ? "Record your first payment to track studio income."
                                : "Try adjusting your search or clearing filters."}
                            </p>
                          </div>
                          {payments.length === 0 && (
                            <button
                              onClick={handleNew}
                              className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-add-line"></i>
                              Record First Payment
                            </button>
                          )}
                          {payments.length > 0 && (
                            <button
                              onClick={() => { setSearch(""); setStatusFilter("all"); setMethodFilter("all"); setDateFrom(""); setDateTo(""); }}
                              className="text-sm text-[#C8A96E] hover:underline cursor-pointer"
                            >
                              Clear all filters
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Result count */}
        {filtered.length > 0 && (
          <p className="text-slate-400 text-xs mt-3 text-right">
            Showing {filtered.length} of {payments.length} payments
          </p>
        )}
      </div>

      {modalOpen && (
        <PaymentModal
          payment={selectedPayment}
          isNew={isNew}
          clients={clientOptions}
          contracts={contractOptions}
          onClose={() => setModalOpen(false)}
          onSave={handleSave}
        />
      )}
    </AdminLayout>
  );
}